import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PackageCheck, ScanLine, Truck, Zap } from "lucide-react";
import { fileUrl } from "@/api/client";

const bg = "https://images.unsplash.com/photo-1589898424451-21e24dcb143c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxwYWNrYWdlJTIwZGVsaXZlcnklMjBtYWlscm9vbSUyMGJveGVzfGVufDB8fHx8MTc4MjY0OTU5Mnww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { user, ready, login, platform } = useAuth();
  const brandName = platform?.brand_name || "Mailroom.OS";
  const tagline = platform?.tagline || "Enterprise mailroom platform";
  const platformLogo = platform?.logo_url;
  const nav = useNavigate();
  const [tenantCode, setTenantCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Delivery Staff Lockdown · Feb 8, 2026 — runners land on My Deliveries.
  const landingForRole = (r) => r === "delivery_staff" ? "/app/drop" : "/app/dashboard";
  if (ready && user) return <Navigate to={landingForRole(user.role)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password, tenantCode);
      toast.success("Signed in");
      nav(landingForRole(data?.user?.role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left visual */}
      <div className="relative hidden lg:block border-r border-border overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center",
            filter: "grayscale(1) contrast(1.1) brightness(0.6)",
          }}
        />
        <div className="absolute inset-0 bg-foreground/70 mix-blend-multiply" />
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="relative z-10 h-full p-12 flex flex-col justify-between text-background">
          <div className="flex items-center gap-3" data-testid="login-brand">
            {platformLogo ? (
              <div className="h-10 max-w-[140px] flex items-center justify-center">
                <img src={fileUrl(platformLogo)} alt={`${brandName} logo`} className="block h-full w-auto max-w-full object-contain" />
              </div>
            ) : (
              <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-display font-bold rounded-md">
                {brandName.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="font-display text-xl">{brandName}</div>
          </div>
          <div className="max-w-lg space-y-6">
            <div className="overline text-primary/90">{tagline}</div>
            <h1 className="text-5xl xl:text-6xl leading-[1.02] font-display font-bold">
              The professional<br /> chain of custody<br />
              <span className="text-primary">for every parcel.</span>
            </h1>
            <p className="text-base text-background/75 max-w-md leading-relaxed">
              Multi-tenant mailroom OS with AI label scan, instant courier rate comparison, employee notifications and signed proof-of-delivery. Deployed on your infrastructure.
            </p>
            <div className="grid grid-cols-3 gap-3 pt-4">
              {[
                { icon: ScanLine, k: "AI OCR", v: "Label scan" },
                { icon: Truck, k: "6+", v: "Couriers" },
                { icon: Zap, k: "<2s", v: "Rate compare" },
              ].map((s) => (
                <div key={s.k} className="border border-background/15 p-3 rounded-md bg-background/5 backdrop-blur-sm">
                  <s.icon size={16} className="text-primary mb-2" />
                  <div className="font-mono text-lg">{s.k}</div>
                  <div className="overline text-background/60">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="overline text-background/60">SOC 2-ready · Auditable · Signed handoffs</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md space-y-8 reveal">
          <div>
            <div className="overline">Sign in</div>
            <h2 className="text-4xl font-display mt-2 font-bold">Welcome back.</h2>
            <p className="text-sm text-muted-foreground mt-2">Enter your credentials to access the operations console.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="overline">Tenant / Company Code</Label>
              <Input
                data-testid="login-tenant"
                type="text" value={tenantCode} onChange={(e) => setTenantCode(e.target.value.toUpperCase())}
                placeholder="e.g. ACME  ·  leave blank for Super Admin"
                className="rounded-md border-input focus-visible:ring-primary mt-1 font-mono"
              />
            </div>
            <div>
              <Label className="overline">Email</Label>
              <Input
                data-testid="login-email"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="rounded-md border-input focus-visible:ring-primary mt-1" required
              />
            </div>
            <div>
              <Label className="overline">Password</Label>
              <Input
                data-testid="login-password"
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="rounded-md border-input focus-visible:ring-primary mt-1" required
              />
            </div>
            <Button
              data-testid="login-submit"
              type="submit" disabled={loading}
              className="w-full rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11 shadow-brut-orange"
            >
              {loading ? "Signing in..." : "Sign in →"}
            </Button>
          </form>

          <div className="border border-border p-4 rounded-md bg-muted/40 flex items-start gap-3" data-testid="security-notice">
            <div className="mt-0.5 text-primary"><Zap size={14} /></div>
            <div className="text-xs text-muted-foreground leading-relaxed">
              <div className="font-semibold text-foreground">Secure sign-in</div>
              First-time access is provisioned by your Platform Admin. If you've lost your password, ask your Company Admin to reset it from <span className="mono">Users → Edit</span>.
            </div>
          </div>

          <div className="text-center text-xs text-muted-foreground pt-2">
            Not staff? <a href="/track" data-testid="login-public-track-link" className="text-primary font-semibold hover:underline">Track a parcel →</a>
          </div>
        </div>
      </div>
    </div>
  );
}
